import { heureka } from "@otto-ec/heureka";
import { eventQBus } from "../types/EventQBus";
import { isActive } from "../toggle/Toggle";
import { resolve, current, type SelectionState } from "@otto-ec/nexus-registry/selection";
import type { FilterSectionLoadEvent } from "@otto-ec/heureka/src";

interface Page {
  sortiertnach?: string;
  l?: string;
  o?: string;
}

interface FilterSectionLoadedEvent {
  rule: string;
  count: number;
}

function facetValueTagsLoaded(rule: string, tags: string[]) {
  return eventQBus.emit("heureka.facetValueTags.loaded", { rule, tags }).then(() => {
    return heureka.facetValueTags_loaded.emit({ rule, tags });
  });
}

function onFacetValueTagsLoaded(callback: (rule: string, tags: string[]) => void) {
  return eventQBus.on("heureka.facetValueTags.loaded", callback);
}

function filterSectionLoadDeprecated(rule: string, page: Page, ssid: string) {
  return eventQBus.emit("heureka.filterSection.load", { rule, page, ssid }).then(() => {
    return heureka.filterSection_load_deprecated.emit({ rule, page, ssid });
  });
}

function filterSectionLoad({ rule, page, ssid, entryRule }: FilterSectionLoadEvent) {
  return eventQBus.emit("heureka.filterSection.load", { rule, page, ssid, entryRule }).then(() => {
    return heureka.filterSection_load.emit({ rule, page, ssid, entryRule });
  });
}

function onFilterSectionLoad(callback: (event: { rule: string; page: Page; ssid: string }) => void) {
  return eventQBus.on("heureka.filterSection.load", callback);
}

function filterSectionLoaded(rule?: string, count?: number) {
  return eventQBus.emit("heureka.filterSection.loaded", { rule, count }).then(() => {
    return heureka.filterSection_loaded.emit();
  });
}

function onFilterSectionLoaded(callback: (event?: FilterSectionLoadedEvent, rootElement?: ParentNode) => void) {
  return eventQBus.on("heureka.filterSection.loaded", callback);
}

function filterSectionInitialLoaded(rule: string, response: Response) {
  return eventQBus.emit("heureka.filterSection.initialLoaded", { rule, response }).then(() => {
    return heureka.filterSection_initialLoaded.emit({ rule, response }, { retain: true });
  });
}

function onFilterSectionInitialLoaded(callback: (event?: { rule: string; response: Response }) => void) {
  if (isActive("HEUREKA_2178_FILTER_SECTION_IS_NOT_LOADED")) {
    return heureka.filterSection_initialLoaded.on(callback);
  } else {
    return eventQBus.on("heureka.filterSection.initialLoaded", callback);
  }
}

function onceFilterSectionInitialLoaded(callback: (event?: { rule: string; response: Response }) => void) {
  return heureka.filterSection_initialLoaded.subscribe(callback, { once: true });
}

function filterSectionLoadAborted() {
  return eventQBus.emit("heureka.filterSection.loadAborted").then(() => {
    return heureka.filterSection_loadAborted.emit();
  });
}

function onFilterSectionLoadAborted(callback: () => void) {
  return eventQBus.on("heureka.filterSection.loadAborted", callback);
}

function refinementBarLoaded() {
  return eventQBus.emit("heureka.refinementBar.loaded").then(() => {
    return heureka.refinementBar_loaded.emit();
  });
}

function onRefinementBarLoaded(callback: () => void) {
  return eventQBus.on("heureka.refinementBar.loaded", callback);
}

function slidersInitialized() {
  return eventQBus.emit("heureka.sliders.initialized").then(() => {
    return heureka.sliders_initialized.emit();
  });
}

function onFilterSheetOpen(callback: () => void) {
  return eventQBus.on("ftfind.filterSheet.open", callback);
}

async function emitDresonRuleResolve(attributes: Record<string, string | string[]>) {
  heureka.dresonRule_resolve.emit();
  try {
    const { count } = await resolve({ attributes });
    if (count === 0) {
      events.emitDresonRuleResolveAborted();
    }
  } catch {
    events.emitDresonRuleResolveAborted();
  }
}

function onDresonRuleResolve(callback: () => void) {
  return heureka.dresonRule_resolve.subscribe(callback);
}

/*                                  */
let currentUnsubscribed = false;
function initDresonRuleResolvedBridge() {
  if (currentUnsubscribed) return;
  currentUnsubscribed = true;
  /*                                                                            */
  /*                                                                                       */
  let replayed = false;
  current.subscribe((state) => {
    if (!replayed) {
      replayed = true;
      return;
    }
    events.emitDresonRuleResolved(state);
  });
}

function emitDresonRuleResolved(data: SelectionState) {
  return heureka.dresonRule_resolved.emit(data);
}

function onDresonRuleResolved(callback: (data?: SelectionState) => void) {
  return heureka.dresonRule_resolved.subscribe(callback);
}

function emitDresonRuleResolveAborted() {
  return heureka.dresonRule_resolveAborted.emit();
}

function onDresonRuleResolveAborted(callback: () => void) {
  return heureka.dresonRule_resolveAborted.subscribe(callback);
}

function onHcSheetRootLoaded(callback: () => void) {
  return eventQBus.on("heureka.hcSheet.rootLoaded", callback);
}

export const events = {
  filterSectionLoadDeprecated,
  filterSectionLoad,
  filterSectionLoaded,
  filterSectionInitialLoaded,
  filterSectionLoadAborted,
  facetValueTagsLoaded,
  refinementBarLoaded,

  slidersInitialized,

  onFilterSectionLoad,
  onFilterSectionLoaded,
  onFilterSectionInitialLoaded,
  onceFilterSectionInitialLoaded,
  onFilterSectionLoadAborted,
  onFacetValueTagsLoaded,
  onRefinementBarLoaded,

  emitDresonRuleResolve,
  onDresonRuleResolve,
  emitDresonRuleResolved,
  onDresonRuleResolved,
  emitDresonRuleResolveAborted,
  onDresonRuleResolveAborted,
  initDresonRuleResolvedBridge,

  onFilterSheetOpen,
  onHcSheetRootLoaded,
};
